import { css } from '@emotion/react';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { Box, FormLabel } from '@mui/material';
import DOMPurify from 'dompurify';
import { $getRoot, $insertNodes, EditorState, LexicalEditor } from 'lexical';
import { useCallback, useEffect, useRef } from 'react';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import SimplifiedToolbarPlugin from './plugins/SimplifiedToolbarPlugin';
import DefaultTheme from './themes/DefaultTheme';
import './editorStyles.css';

const containerCss = css`
  border: 1px solid var(--mui-colors-neutral-30);
  border-radius: 8px;
  overflow: hidden;
`;

const editorContainerCss = css`
  position: relative;
  background: #fff;
`;

const editorInputCss = css`
  min-height: 100px;
  padding: 12px;
  outline: none;
  font-size: 14px;
  line-height: 1.5;

  p {
    margin: 0 0 8px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const placeholderCss = css`
  color: #999;
  overflow: hidden;
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 14px;
  user-select: none;
  pointer-events: none;
`;

interface SimplifiedRichTextEditorProps {
  label: string;
  value?: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

function HtmlInitializerPlugin({ initialHtml }: { initialHtml?: string }) {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Skip if value hasn't changed
    if (lastValueRef.current === initialHtml) {
      return;
    }
    lastValueRef.current = initialHtml;

    editor.update(
      () => {
        const root = $getRoot();
        root.clear();

        if (initialHtml) {
          const parser = new DOMParser();
          const dom = parser.parseFromString(initialHtml, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          $insertNodes(nodes);
        }
      },
      { discrete: true }
    );
  }, [editor, initialHtml]);

  return null;
}

function Placeholder({ text }: { text: string }) {
  return <div css={placeholderCss}>{text}</div>;
}

export function SimplifiedRichTextEditor({
  label,
  value,
  onChange,
  placeholder = 'Enter text...',
}: SimplifiedRichTextEditorProps) {
  const editorRef = useRef<LexicalEditor | null>(null);

  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        const sanitized = DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'b', 'i'],
          ALLOWED_ATTR: ['href', 'style'],
        });
        onChange(sanitized);
      });
    },
    [onChange]
  );

  const initialConfig = {
    namespace: `SimplifiedEditor-${label}`,
    theme: DefaultTheme,
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    nodes: [AutoLinkNode, LinkNode],
    editorState: null,
  };

  return (
    <Box>
      <FormLabel sx={{ display: 'block', mb: 0.5, fontSize: '0.875rem' }}>{label}</FormLabel>
      <div css={containerCss}>
        <LexicalComposer initialConfig={initialConfig}>
          <SimplifiedToolbarPlugin />
          <div css={editorContainerCss}>
            <RichTextPlugin
              contentEditable={<ContentEditable css={editorInputCss} />}
              placeholder={<Placeholder text={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <OnChangePlugin
              onChange={(editorState, editor) => {
                editorRef.current = editor;
                handleChange(editorState, editor);
              }}
            />
            <HistoryPlugin />
            <AutoLinkPlugin />
            <LinkPlugin />
            <HtmlInitializerPlugin initialHtml={value} />
          </div>
        </LexicalComposer>
      </div>
    </Box>
  );
}
